import styled, { createGlobalStyle, css } from 'styled-components';

import { Colors } from 'services/ColorService';

import { TNotificationTone } from './notificationTypes';

/**
 * Вёрстка колокольчика (ТЗ 0068).
 *
 * `z-index` здесь НЕ пишется нигде и писать его не нужно: панель живёт в
 * поповере antd, а тот уходит в `document.body` со своим слоем выше шапки.
 * Голое число больше 100 вне allow-list роняет сторож `layers.guard`.
 */

/** Кнопка-колокольчик. Копия `PaletteTrigger` из шапки: две иконочные кнопки
 *  рядом обязаны выглядеть одинаково, иначе шапка «рассыпается». */
export const BellButton = styled.button<{ $active: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 1px solid
    ${({ $active }) => ($active ? Colors.Blue : Colors.SecondLightGray)};
  border-radius: 8px;
  cursor: pointer;
  color: ${({ $active }) => ($active ? Colors.Blue : Colors.Gray)};
  background: ${Colors.White};
  transition: color 0.15s ease, border-color 0.15s ease;

  &:hover {
    color: ${Colors.Blue};
    border-color: ${Colors.Blue};
  }
`;

/** Накладной счётчик — по образцу `MiniBadge` коммуникатора. */
export const Badge = styled.span`
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: #2563eb;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 2px solid ${Colors.White};
`;

/** Янтарная точка: звук включён, но браузер его ещё не разрешил. */
export const LockedDot = styled.span`
  position: absolute;
  top: -3px;
  right: -3px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #d97706;
  border: 2px solid ${Colors.White};
`;

export const PopoverStyles = createGlobalStyle`
  .notification-center-popover .ant-popover-inner-content {
    padding: 0;
  }
  .notification-center-popover .ant-popover-arrow {
    display: none;
  }
`;

export const Panel = styled.div`
  width: 420px;
  max-width: calc(100vw - 32px);
  display: flex;
  flex-direction: column;
  max-height: min(70vh, 560px);
`;

export const PanelHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border-bottom: 1px solid ${Colors.LightGray};
`;

export const PanelTitle = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: ${Colors.Black};
`;

export const CountPill = styled.span`
  min-width: 20px;
  padding: 1px 7px;
  border-radius: 999px;
  background: #2563eb;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
`;

export const HeadSpacer = styled.div`
  flex: 1;
`;

export const TextButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  color: ${Colors.Blue};
  font-size: 12px;

  &:disabled {
    color: ${Colors.Gray};
    cursor: default;
  }
`;

export const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  padding: 2px;
  cursor: pointer;
  color: ${Colors.Gray};

  &:hover {
    color: ${Colors.Blue};
  }
`;

export const Tabs = styled.div`
  display: flex;
  gap: 4px;
  padding: 8px 14px;
  border-bottom: 1px solid ${Colors.LightGray};
`;

export const Tab = styled.button<{ $active: boolean }>`
  border: none;
  cursor: pointer;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  background: ${({ $active }) => ($active ? Colors.MainBackground : 'none')};
  color: ${({ $active }) => ($active ? Colors.Black : Colors.Gray)};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
`;

export const Warning = styled.div`
  padding: 8px 14px;
  background: #fff7ed;
  color: #92400e;
  font-size: 12px;
  border-bottom: 1px solid #fed7aa;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const List = styled.div`
  overflow-y: auto;
  flex: 1;
`;

const TONE_COLOR: Record<TNotificationTone, string> = {
  accent: '#2563eb',
  warn: '#d97706',
  danger: '#dc2626',
};

export const Row = styled.button<{ $unread: boolean }>`
  width: 100%;
  display: grid;
  grid-template-columns: 40px 1fr auto;
  gap: 10px;
  align-items: start;
  padding: 10px 14px;
  border: none;
  border-bottom: 1px solid ${Colors.LightGray};
  cursor: pointer;
  text-align: left;
  background: ${({ $unread }) => ($unread ? '#f4f7ff' : Colors.White)};

  &:hover {
    background: ${Colors.MainBackground};
  }
`;

export const RowIcon = styled.span<{ $tone: TNotificationTone }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: ${({ $tone }) => TONE_COLOR[$tone]};
  background: ${({ $tone }) => `${TONE_COLOR[$tone]}14`};
`;

export const RowBody = styled.div`
  min-width: 0;
`;

export const RowTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${Colors.Black};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const RowText = styled.div`
  font-size: 12px;
  color: ${Colors.Gray};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const RowTime = styled.div`
  font-size: 11px;
  color: ${Colors.Gray};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

export const Empty = styled.div`
  padding: 32px 14px;
  text-align: center;
  color: ${Colors.Gray};
  font-size: 13px;
`;

export const PanelFoot = styled.div`
  padding: 10px 14px;
  border-top: 1px solid ${Colors.LightGray};
  display: flex;
  align-items: center;
  gap: 12px;
`;

/* ── Настройки ─────────────────────────────────────────────────────────── */

export const Pane = styled.div`
  padding: 14px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const Field = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
  color: ${Colors.Black};
`;

export const FieldHint = styled.div`
  font-size: 11px;
  color: ${Colors.Gray};
  margin-top: 2px;
`;

export const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  ${() => css`
    & + & {
      padding-top: 12px;
      border-top: 1px solid ${Colors.LightGray};
    }
  `}
`;

export const SectionTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${Colors.Gray};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

export const Saved = styled.div`
  font-size: 12px;
  color: ${Colors.SuccessText};
`;

export const Failed = styled.div`
  font-size: 12px;
  color: ${Colors.ErrorText};
`;

export const Mono = styled.div`
  font-family: SFMono-Regular, Consolas, monospace;
  font-size: 11px;
  background: ${Colors.MainBackground};
  border-radius: 6px;
  padding: 8px;
  word-break: break-all;
  color: ${Colors.Black};
`;

export const InlineActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;
